import {
  Accordion,
  Box,
  ColorInput,
  Fieldset,
  Grid,
  Group,
  NumberInput,
  Select,
  Stack,
  TextInput,
  Tooltip,
} from '@mantine/core';
import { IconDeviceDesktop } from '@tabler/icons-react';
import { useState } from 'react';
import { MODAL_DISPLAY_TYPES, cardOffsetError, cardSizeError, resolvedCardPlacement } from '../constants/bannerChrome';
import CardPlacementPicker from './CardPlacementPicker';

// What an offset means depends on which edge the card is anchored to, and the
// author is the one who just chose that edge: `24px` from a card pinned to the
// bottom moves it up, and from a centred one moves it down. Two fields labelled
// "Vertical offset" and "Horizontal offset" left them to work that out from the
// preview.
const OFFSET_LABEL = {
  vertical: { top: 'Gap from top', center: 'Move down by', bottom: 'Gap from bottom' },
  horizontal: { left: 'Gap from left', center: 'Move right by', right: 'Gap from right' },
};

const CLOSE_BUTTON_SECTION = 'closeButton';

export const WEB_ONLY_SETTING_TOOLTIP =
  'Applies on your website only. The mobile apps ignore this setting and keep their own fixed close button.';

const WEB_ONLY_MARKER_LABEL = 'Websites only';

// Six of the eleven close-button settings are web only — the mobile SDKs read
// colour, background, border, font size and rounding and nothing else — so
// without a marker the other six read as settings the apps simply got wrong.
const webOnlyLabel = (text) => (
  <Group component="span" gap={4} wrap="nowrap">
    {text}
    <Tooltip
      label={WEB_ONLY_SETTING_TOOLTIP}
      multiline
      maw={260}
      withArrow
      events={{ hover: true, focus: true, touch: true }}
    >
      <Box
        component="span"
        style={{ display: 'inline-flex', cursor: 'help' }}
        role="img"
        aria-label={WEB_ONLY_MARKER_LABEL}
        tabIndex={0}
      >
        <IconDeviceDesktop size={14} color="var(--mantine-color-dimmed)" />
      </Box>
    </Tooltip>
  </Group>
);

const PageBlockAdvancedStyling = ({ pageBlock, handleStateChange, displayPositionPersisted }) => {
  // The close button is twelve fields that almost every block leaves at the
  // defaults, and they used to sit between the author and the card controls
  // they came for. Folded away, with the button itself drawn on the control so
  // that a block whose author *has* styled it still shows what they did.
  const [closeButtonOpen, setCloseButtonOpen] = useState(false);
  // A pop-up is the only mode drawn over a dimmed page, and a bar is always the
  // full width of the viewport. The SDK ignores `overlayColor` and `cardWidth`
  // respectively for the rest, so offering those controls there would be
  // offering a setting that changes nothing.
  const isPopup = pageBlock.displayType === 'popup';
  const isBar = pageBlock.displayType === 'bar';
  const isFlyout = pageBlock.displayType === 'flyout';
  // The card the position controls place only exists for the modes the SDK
  // draws one for; `static` and `custom` are inserted into the page, so there
  // is nothing for them to position.
  const isModal = MODAL_DISPLAY_TYPES.includes(pageBlock.displayType);

  // `displayPosition` is the only position the legacy control writes, and the
  // mobile SDKs compare it by exact string equality. Where an axis lands on a
  // value that column's vocabulary can express — a bar's top/bottom, a
  // flyout's left/right — it is written through as well, so the legacy
  // control does not end up contradicting this panel. Anything else (a
  // centred bar, a positioned pop-up) is expressible only through the two
  // axes, which every SDK now reads.
  //
  // Only for a block whose model actually has that column, though:
  // `displayPositionPersisted` is false for a recommender, where the write
  // would move this panel and the preview and then snap back when the save's
  // response — which dropped the field — replaces the form's state.
  const handlePositionChange = (name, value) => {
    handleStateChange({ target: { name, value } });
    if (!displayPositionPersisted) return;
    if (
      (isBar && name === 'cardPositionVertical' && (value === 'top' || value === 'bottom')) ||
      (isFlyout && name === 'cardPositionHorizontal' && (value === 'left' || value === 'right'))
    ) {
      handleStateChange({ target: { name: 'displayPosition', value } });
    }
  };

  // The picker hands back the axes it wants written — one for a bar, two for
  // anything else — and each goes through the mirror above, so a gesture on the
  // grid reaches `displayPosition` exactly where a gesture on the dropdowns
  // this replaced used to.
  const handlePlacementChange = (change) => {
    Object.entries(change).forEach(([name, value]) => handlePositionChange(name, value));
  };

  // The offsets are free text on a closed unit set of their own — wider than
  // the card sizes', since a gap from an edge can be a bare `0` or negative.
  // A cleared field reports `''`, which is wrong on both sides of the wire in
  // the same way it is for `cardWidth` — normalised to the documented unset
  // value instead, for the same reason.
  // Normalised on blur, not on change, for the same reason `cardWidth` and
  // `cardHeight` below are: `onChange` fires per keystroke, so clearing `24px`
  // to retype it would put `auto` back under the cursor and the next character
  // would append to it (`auto5`, `auto5%`). `''` only exists while the field
  // has focus, and every save is a click or a tab away from it.
  const offsetProps = (name) => ({
    name,
    value: pageBlock.cssStyles[name] ?? '',
    placeholder: 'auto',
    error: cardOffsetError(pageBlock.cssStyles[name]),
    onChange: (e) => handleStateChange(e),
    onBlur: (e) => {
      if (e.target.value === '') {
        handleStateChange({ target: { name, value: 'auto' } });
      }
    },
  });

  // Where the card sits once `auto` has been resolved — the same answer the SDK
  // arrives at — which is what decides what each offset is a distance from.
  const placement = resolvedCardPlacement({
    displayType: pageBlock.displayType,
    displayPosition: pageBlock.displayPosition,
    cssStyles: pageBlock.cssStyles,
  });

  const closeButtonPreview = (
    <span
      style={{
        display: 'inline-block',
        border: pageBlock.cssStyles.closeButtonBorder || undefined,
        color: pageBlock.cssStyles.closeButtonColor,
        backgroundColor: pageBlock.cssStyles.closeButtonBackgroundColor,
        borderRadius: `${pageBlock.cssStyles.closeButtonBorderRadius}px`,
        padding: pageBlock.cssStyles.closeButtonPadding,
        fontSize: `${pageBlock.cssStyles.closeFontSize}px`,
        lineHeight: `${pageBlock.cssStyles.closeButtonLineHeight}px`,
        fontWeight: pageBlock.cssStyles.closeButtonFontWeight,
      }}
    >
      {pageBlock.cssStyles.closeButtonSymbol || 'X'}
    </span>
  );

  return (
    <Stack gap="md">
      {isModal && (
        <Fieldset legend="Placement" p="md" pt="xs">
          <Grid>
            <Grid.Col span={{ base: 12, sm: 5 }}>
              <CardPlacementPicker
                displayType={pageBlock.displayType}
                displayPosition={pageBlock.displayPosition}
                cssStyles={pageBlock.cssStyles}
                onChange={handlePlacementChange}
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 7 }}>
              <Stack gap="xs">
                <TextInput label={OFFSET_LABEL.vertical[placement.vertical]} {...offsetProps('cardOffsetVertical')} />
                {/* A bar spans the viewport, so it has no horizontal axis to place
                    it on and no horizontal edge to offset it from. */}
                {!isBar && (
                  <TextInput
                    label={OFFSET_LABEL.horizontal[placement.horizontal]}
                    {...offsetProps('cardOffsetHorizontal')}
                  />
                )}
              </Stack>
            </Grid.Col>
          </Grid>
        </Fieldset>
      )}

      <Fieldset legend="Card" p="md" pt="xs">
        <Grid>
          {isPopup && (
            <Grid.Col span={{ base: 12, sm: 6 }}>
              <ColorInput
                label="Overlay color"
                name="overlayColor"
                format="rgba"
                value={pageBlock.cssStyles.overlayColor ?? ''}
                onChange={(value) => handleStateChange({ target: { name: 'overlayColor', value } })}
                // See `cardBackgroundColor` below — the four colour fields
                // share this control and the same reason to disable it.
                fixOnBlur={false}
              />
            </Grid.Col>
          )}
          <Grid.Col span={{ base: 12, sm: 6 }}>
            <ColorInput
              label="Card background"
              name="cardBackgroundColor"
              value={pageBlock.cssStyles.cardBackgroundColor ?? ''}
              onChange={(value) => handleStateChange({ target: { name: 'cardBackgroundColor', value } })}
              // Mantine's default `fixOnBlur` replays `onChange(lastValidValue)`
              // on every blur, touched or not, because the control is
              // controlled (a `value` prop is always supplied) and
              // `useUncontrolled` calls that `onChange` directly with no
              // equality guard (`node_modules/@mantine/hooks/esm/use-uncontrolled/use-uncontrolled.mjs:10-14`).
              // `lastValidValue` starts as `''` and an empty string counts as
              // "valid" for its tracking effect
              // (`node_modules/@mantine/core/esm/components/ColorInput/ColorInput.mjs:106`),
              // so tabbing past an untouched field writes `''` even though
              // nothing was edited. For `cardBackgroundColor` that is not a
              // no-op: unlike the three close-button/overlay colours, it is
              // deliberately absent from `EMPTY_CHROME_CSS_STYLES` (see that
              // constant's comment), and `pruneUnreachableCardCssStyles`
              // does not strip it — so an absent key becomes a stored `''`,
              // which the SDK resolves instead of falling back to its
              // default, emitting `background-color: ;` and a transparent
              // card. Disabling the replay costs only the one case it existed
              // for — reverting an invalid typed value on blur — which
              // nothing here relied on, since an untouched field has nothing
              // to normalise to.
              fixOnBlur={false}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 6 }}>
            <NumberInput
              label="Card rounding"
              name="cardBorderRadius"
              value={pageBlock.cssStyles.cardBorderRadius ?? ''}
              placeholder="0"
              // A trailing decimal point (typing "1.") makes NumberInput hand
              // back the raw, still-incomplete string instead of a number —
              // `allowDecimal={false}` keeps that state unreachable, since a
              // unitless radius has no use for a fraction anyway.
              allowDecimal={false}
              // `allowNegative` defaults to true, and a bare "-" (no digits
              // yet) also reaches `onChange` as the raw string "-" rather than
              // a number — both a typed "-5" and a lone "-" would otherwise
              // write a negative or malformed radius the API rejects and CSS
              // would drop anyway. `min={0}` alone would not close this:
              // Mantine's default `clampBehavior` is 'blur', so the
              // intermediate value still reaches `onChange` first.
              allowNegative={false}
              // Unitless, and a string: the SDK appends the `px` and the API
              // rejects the bare number `NumberInput` hands back. Clearing the
              // field reports the empty string rather than a number — written
              // as-is that would be an equally bare, equally rejected value, so
              // it is normalised to the unset default instead.
              onChange={(value) =>
                handleStateChange({ target: { name: 'cardBorderRadius', value: value === '' ? '0' : String(value) } })
              }
            />
          </Grid.Col>
          {!isBar && (
            <Grid.Col span={{ base: 12, sm: 6 }}>
              <TextInput
                label="Card width"
                name="cardWidth"
                value={pageBlock.cssStyles.cardWidth ?? ''}
                placeholder="auto"
                error={cardSizeError(pageBlock.cssStyles.cardWidth)}
                onChange={(e) => handleStateChange(e)}
                // An emptied field is `''`, a valid-looking value that is
                // wrong on both sides of the wire — `cardSizeError('')`
                // reads it as "unset" and stays quiet, but the API's rule
                // for this key is `auto` or a sized string — so it is
                // normalised to the `auto` the placeholder already shows.
                // On blur rather than on change: `onChange` fires per
                // keystroke, so clearing `480px` to retype it would put
                // `auto` back under the cursor and the next character would
                // append to it (`auto7`, `auto70%`). `''` only exists while
                // the field has focus, and every save is a click or a tab
                // away from it, so the blur lands first.
                onBlur={(e) => {
                  if (e.target.value === '') {
                    handleStateChange({ target: { name: 'cardWidth', value: 'auto' } });
                  }
                }}
              />
            </Grid.Col>
          )}
          <Grid.Col span={{ base: 12, sm: 6 }}>
            <TextInput
              label="Card height"
              name="cardHeight"
              value={pageBlock.cssStyles.cardHeight ?? ''}
              placeholder="auto"
              error={cardSizeError(pageBlock.cssStyles.cardHeight)}
              onChange={(e) => handleStateChange(e)}
              // See `cardWidth` above for why a cleared field is normalised on
              // blur rather than sent through as `''` or fixed up mid-edit.
              onBlur={(e) => {
                if (e.target.value === '') {
                  handleStateChange({ target: { name: 'cardHeight', value: 'auto' } });
                }
              }}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 6 }}>
            <Select
              label="Content alignment"
              name="contentVerticalAlign"
              value={pageBlock.cssStyles.contentVerticalAlign ?? null}
              // Mantine's default lets clicking the already-selected option
              // clear it, firing `onChange(null)` — which would write `null`
              // into an API-validated enum. There is no "unset" for this key
              // to fall back to (unlike the empty-string keys), so the control
              // itself must not be able to produce one.
              allowDeselect={false}
              onChange={(value) => handleStateChange({ target: { name: 'contentVerticalAlign', value } })}
              data={[
                { label: 'Top', value: 'top' },
                { label: 'Center', value: 'center' },
                { label: 'Bottom', value: 'bottom' },
              ]}
            />
          </Grid.Col>
        </Grid>
      </Fieldset>

      <Accordion
        variant="contained"
        value={closeButtonOpen ? CLOSE_BUTTON_SECTION : null}
        onChange={(value) => setCloseButtonOpen(value === CLOSE_BUTTON_SECTION)}
      >
        <Accordion.Item value={CLOSE_BUTTON_SECTION}>
          <Accordion.Control>
            <Group gap="sm">
              <span>Close button</span>
              {closeButtonPreview}
            </Group>
          </Accordion.Control>
          <Accordion.Panel>
            <Grid>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <TextInput
                  label={webOnlyLabel('Text')}
                  name="closeButtonSymbol"
                  value={pageBlock.cssStyles.closeButtonSymbol}
                  placeholder="X"
                  onChange={(e) => handleStateChange(e)}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <ColorInput
                  label="Color"
                  name="closeButtonColor"
                  value={pageBlock.cssStyles.closeButtonColor ?? ''}
                  onChange={(value) => handleStateChange({ target: { name: 'closeButtonColor', value } })}
                  // See `cardBackgroundColor` above — same control, same
                  // untouched-blur write, so the same guard even though this
                  // key is normally seeded blank rather than absent.
                  fixOnBlur={false}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <ColorInput
                  label="Background"
                  name="closeButtonBackgroundColor"
                  value={pageBlock.cssStyles.closeButtonBackgroundColor ?? ''}
                  onChange={(value) => handleStateChange({ target: { name: 'closeButtonBackgroundColor', value } })}
                  fixOnBlur={false}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <TextInput
                  label="Border"
                  name="closeButtonBorder"
                  value={pageBlock.cssStyles.closeButtonBorder}
                  placeholder="1px solid #fff"
                  onChange={(e) => handleStateChange(e)}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <NumberInput
                  label="Rounding"
                  name="closeButtonBorderRadius"
                  value={pageBlock.cssStyles.closeButtonBorderRadius}
                  placeholder="20"
                  onChange={(value) => handleStateChange({ target: { name: 'closeButtonBorderRadius', value } })}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <TextInput
                  label={webOnlyLabel('Padding')}
                  name="closeButtonPadding"
                  value={pageBlock.cssStyles.closeButtonPadding}
                  placeholder="4px 5px"
                  onChange={(e) => handleStateChange(e)}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <NumberInput
                  label="Font size"
                  name="closeFontSize"
                  value={pageBlock.cssStyles.closeFontSize}
                  placeholder="14"
                  onChange={(value) => handleStateChange({ target: { name: 'closeFontSize', value } })}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <NumberInput
                  label={webOnlyLabel('Line height')}
                  name="closeButtonLineHeight"
                  value={pageBlock.cssStyles.closeButtonLineHeight}
                  placeholder="14"
                  onChange={(value) => handleStateChange({ target: { name: 'closeButtonLineHeight', value } })}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <Select
                  label={webOnlyLabel('Font weight')}
                  name="closeButtonFontWeight"
                  value={pageBlock.cssStyles.closeButtonFontWeight}
                  onChange={(value) => handleStateChange({ target: { name: 'closeButtonFontWeight', value } })}
                  data={[
                    { label: 'Light', value: '200' },
                    { label: 'Normal', value: '400' },
                    { label: 'Bold', value: '600' },
                    { label: 'Bolder', value: '800' },
                  ]}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 3 }}>
                <NumberInput
                  label={webOnlyLabel('Offset top')}
                  name="closeButtonTopPosition"
                  value={pageBlock.cssStyles.closeButtonTopPosition}
                  placeholder="-14"
                  onChange={(value) => handleStateChange({ target: { name: 'closeButtonTopPosition', value } })}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 3 }}>
                <NumberInput
                  label={webOnlyLabel('Offset right')}
                  name="closeButtonRightPosition"
                  value={pageBlock.cssStyles.closeButtonRightPosition}
                  placeholder="-14"
                  onChange={(value) => handleStateChange({ target: { name: 'closeButtonRightPosition', value } })}
                />
              </Grid.Col>
            </Grid>
          </Accordion.Panel>
        </Accordion.Item>
      </Accordion>
    </Stack>
  );
};

export default PageBlockAdvancedStyling;
